import { DynamicBooleanSchema, DynamicStringListSchema, DynamicStringSchema } from '@a2ui/web_core/v0_9';
import {
  ButtonApi,
  CardApi,
  CheckBoxApi,
  ChoicePickerApi,
  ColumnApi,
  DateTimeInputApi,
  RowApi,
  TextApi,
  TextFieldApi,
} from '@a2ui/web_core/v0_9/basic_catalog';
import { CatalogRendererProps, createCatalog } from '@copilotkit/a2ui-renderer';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import React from 'react';
import { z } from 'zod';

import Button from '~/components/Button';
import SecondaryButton from '~/components/Button/SecondaryButton';
import Checkbox from '~/components/Checkbox';
import DateFilterButton from '~/components/DateFilterButton';
import DateTimeInput from '~/components/DateTimeInput';
import Dropdown from '~/components/Dropdown';
import Input from '~/components/Input';
import MultiEmailInput from '~/components/MultiEmailInput';
import MultiTagInput from '~/components/MultiTagInput';
import PhoneInput from '~/components/PhoneInput';
import Switch from '~/components/Switch';

import { VisitiqGeoFilter } from './GeoFilterField';
import { VISITIQ_CATALOG_ID } from './operationsEnvelope';
import { CatalogCard, CatalogColumn, CatalogRow, CatalogText, ErrorLine, FieldLabel } from './visitiqCatalog.styles';

const moment = extendMoment(Moment);
const ISO_DATE = 'YYYY-MM-DD';

// Fields common to every bindable component once the binder resolves them.
interface CheckableProps {
  validationErrors?: string[];
  isValid?: boolean;
}

interface TextFieldRendererProps extends CheckableProps {
  label?: string;
  value?: string;
  variant?: 'longText' | 'number' | 'shortText' | 'obscured';
  setValue: (value: string) => void;
}

const VisitiqTextField = ({ props }: CatalogRendererProps<TextFieldRendererProps>): JSX.Element => (
  <Input
    fullWidth
    // Drop the Input's default large bottom margin ($8); the Column gap handles spacing so the
    // gap to the next element (e.g. the submit button) isn't oversized.
    marginBottom="0"
    label={props.label}
    type={props.variant === 'number' ? 'number' : props.variant === 'obscured' ? 'password' : 'text'}
    value={props.value ?? ''}
    error={props.validationErrors?.[0]}
    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => props.setValue(event.target.value)}
  />
);

interface CheckBoxRendererProps extends CheckableProps {
  label?: string;
  value?: boolean;
  setValue: (value: boolean) => void;
}

const VisitiqCheckBox = ({ props }: CatalogRendererProps<CheckBoxRendererProps>): JSX.Element => (
  <div>
    <Checkbox isSelected={!!props.value} onChange={(checked: boolean) => props.setValue(checked)}>
      {props.label ?? ''}
    </Checkbox>
    {props.validationErrors?.[0] && <ErrorLine>{props.validationErrors[0]}</ErrorLine>}
  </div>
);

interface ChoicePickerRendererProps extends CheckableProps {
  label?: string;
  variant?: 'multipleSelection' | 'mutuallyExclusive';
  options?: Array<{ label: string; value: string }>;
  value?: string[];
  setValue: (values: string[]) => void;
}

const VisitiqChoicePicker = ({ props }: CatalogRendererProps<ChoicePickerRendererProps>): JSX.Element => {
  const options = props.options ?? [];
  const values = Array.isArray(props.value) ? props.value : [];
  const multiselect = props.variant === 'multipleSelection';
  const selectedIndexes = options.flatMap((option, index) => (values.includes(option.value) ? [index] : []));

  return (
    <Dropdown
      title={props.label}
      placeholder={props.label}
      items={options.map((option) => ({ key: option.value, text: option.label, value: option.value }))}
      multiselect={multiselect}
      selectedIndex={selectedIndexes[0]}
      selectedIndexes={multiselect ? selectedIndexes : undefined}
      error={props.validationErrors?.[0]}
      onSelectionChange={(selection) => {
        const indices = Array.isArray(selection) ? selection : [selection];
        props.setValue(
          indices.map((index) => options[index]?.value).filter((value): value is string => value !== undefined),
        );
      }}
    />
  );
};

interface DateTimeInputRendererProps extends CheckableProps {
  value?: string;
  setValue: (value: string) => void;
}

const VisitiqDateTimeInput = ({ props }: CatalogRendererProps<DateTimeInputRendererProps>): JSX.Element => (
  <DateTimeInput
    // The A2UI value is an ISO string ('' until the user picks); the VisitIQ component
    // tolerates null at runtime even though its prop is typed as Date.
    value={props.value ? moment(props.value).toDate() : (null as unknown as Date)}
    error={props.validationErrors?.[0]}
    onChange={(date) => props.setValue(date ? moment(date).format(ISO_DATE) : '')}
  />
);

interface ButtonRendererProps extends CheckableProps {
  child?: string;
  variant?: 'default' | 'primary' | 'borderless';
  action?: () => void;
}

const VisitiqButton = ({ props, children }: CatalogRendererProps<ButtonRendererProps>): JSX.Element => {
  const disabled = props.isValid === false;
  const isPrimary = props.variant === 'primary';
  const ButtonComponent = isPrimary ? Button : SecondaryButton;
  return (
    <ButtonComponent
      // Size to content and don't stretch: the button sits inside a Column (align-items:
      // stretch) which would otherwise blow it up to full width.
      auto
      disableMinWidth
      css={{ width: 'fit-content', alignSelf: 'flex-start', ...(disabled && { pointerEvents: 'none' }) }}
      color={isPrimary ? 'primary' : undefined}
      disabled={disabled}
      // Guard the handler too: the app Button fires onPress via onPressEnd on touch devices,
      // so rely on the explicit check rather than the disabled prop alone.
      onPress={() => {
        if (disabled) {
          return;
        }
        props.action?.();
      }}
    >
      {props.child ? children(props.child) : null}
    </ButtonComponent>
  );
};

interface DateRangePickerRendererProps extends CheckableProps {
  label?: string;
  startValue?: string;
  endValue?: string;
  setStartValue: (value: string) => void;
  setEndValue: (value: string) => void;
}

const VisitiqDateRangePicker = ({ props }: CatalogRendererProps<DateRangePickerRendererProps>): JSX.Element => (
  <div>
    {props.label && <FieldLabel>{props.label}</FieldLabel>}
    <DateFilterButton
      dateRange={
        props.startValue && props.endValue ? moment.range(moment(props.startValue), moment(props.endValue)) : undefined
      }
      onRangeSelect={(range) => {
        props.setStartValue(range.start.format(ISO_DATE));
        props.setEndValue(range.end.format(ISO_DATE));
      }}
    />
    {props.validationErrors?.[0] && <ErrorLine>{props.validationErrors[0]}</ErrorLine>}
  </div>
);

interface SwitchRendererProps extends CheckableProps {
  title?: string;
  value?: boolean;
  setValue: (value: boolean) => void;
}

const VisitiqSwitch = ({ props }: CatalogRendererProps<SwitchRendererProps>): JSX.Element => (
  <div>
    <Switch checked={!!props.value} title={props.title} onChange={() => props.setValue(!props.value)} />
    {props.validationErrors?.[0] && <ErrorLine>{props.validationErrors[0]}</ErrorLine>}
  </div>
);

interface MultiTagInputRendererProps extends CheckableProps {
  label?: string;
  value?: string[];
  setValue: (tags: string[]) => void;
}

const VisitiqMultiTagInput = ({ props }: CatalogRendererProps<MultiTagInputRendererProps>): JSX.Element => (
  <MultiTagInput
    label={props.label}
    tags={Array.isArray(props.value) ? props.value : []}
    error={props.validationErrors?.[0]}
    onChange={(tags) => props.setValue(tags)}
    onRemove={(tags) => props.setValue(tags)}
  />
);

interface MultiEmailInputRendererProps extends CheckableProps {
  label?: string;
  value?: string[];
  setValue: (tags: string[]) => void;
}

const VisitiqMultiEmailInput = ({ props }: CatalogRendererProps<MultiEmailInputRendererProps>): JSX.Element => (
  <MultiEmailInput
    label={props.label}
    emails={Array.isArray(props.value) ? props.value : []}
    error={props.validationErrors?.[0]}
    disabled={false}
    onChange={(emails: string[]) => props.setValue(emails)}
    onRemove={(emails: string[]) => props.setValue(emails)}
  />
);

interface PhoneInputRendererProps extends CheckableProps {
  label?: string;
  value?: string;
  setValue: (value: string) => void;
}

const VisitiqPhoneInput = ({ props }: CatalogRendererProps<PhoneInputRendererProps>): JSX.Element => (
  <PhoneInput
    label={props.label}
    value={props.value ?? ''}
    error={props.validationErrors?.[0]}
    onChange={(phone, isComplete) => props.setValue(isComplete ? phone : '')}
  />
);

interface SecretInputRendererProps extends CheckableProps {
  label?: string;
  value?: string;
  setValue: (value: string) => void;
}

const VisitiqSecretInput = ({ props }: CatalogRendererProps<SecretInputRendererProps>): JSX.Element => (
  <Input
    fullWidth
    marginBottom="0"
    type="password"
    autoComplete="off"
    label={props.label}
    value={props.value ?? ''}
    error={props.validationErrors?.[0]}
    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => props.setValue(event.target.value)}
  />
);

interface TextRendererProps {
  text?: string;
}

const VisitiqText = ({ props }: CatalogRendererProps<TextRendererProps>): JSX.Element => (
  <CatalogText>{props.text ?? ''}</CatalogText>
);

const ROW_JUSTIFY_CONTENT = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  spaceAround: 'space-around',
  spaceBetween: 'space-between',
  spaceEvenly: 'space-evenly',
  stretch: 'stretch',
} as const;

interface RowRendererProps {
  children?: string[];
  justify?: keyof typeof ROW_JUSTIFY_CONTENT;
}

const VisitiqRow = ({ props, children }: CatalogRendererProps<RowRendererProps>): JSX.Element => (
  <CatalogRow $justify={props.justify ? ROW_JUSTIFY_CONTENT[props.justify] : undefined}>
    {(props.children ?? []).map((id) => (
      <React.Fragment key={id}>{children(id)}</React.Fragment>
    ))}
  </CatalogRow>
);

interface ColumnRendererProps {
  children?: string[];
}

const VisitiqColumn = ({ props, children }: CatalogRendererProps<ColumnRendererProps>): JSX.Element => (
  <CatalogColumn>
    {(props.children ?? []).map((id) => (
      <React.Fragment key={id}>{children(id)}</React.Fragment>
    ))}
  </CatalogColumn>
);

interface CardRendererProps {
  child?: string;
}

const VisitiqCard = ({ props, children }: CatalogRendererProps<CardRendererProps>): JSX.Element => (
  <CatalogCard>{props.child ? children(props.child) : null}</CatalogCard>
);

export const visitiqCatalog = createCatalog(
  {
    // Overrides: same names + schemas as the basic catalog, VisitIQ look & feel.
    TextField: { props: TextFieldApi.schema },
    CheckBox: { props: CheckBoxApi.schema },
    ChoicePicker: { props: ChoicePickerApi.schema },
    DateTimeInput: { props: DateTimeInputApi.schema },
    Button: { props: ButtonApi.schema },
    Text: { props: TextApi.schema },
    Row: { props: RowApi.schema },
    Column: { props: ColumnApi.schema },
    Card: { props: CardApi.schema },
    // VisitIQ-specific components (no basic equivalent). No .strict(): wire components
    // carry common fields (id/component/weight) the basic schemas allow via CommonProps.
    VIQDateRangePicker: {
      description:
        'VisitIQ date range picker (button opening a calendar modal). Use for start/end date ' +
        'ranges instead of two separate DateTimeInputs.',
      props: z.object({
        label: DynamicStringSchema.describe('Label shown above the picker.').optional(),
        startValue: DynamicStringSchema.describe(
          'Range start date, ISO 8601 (YYYY-MM-DD). Bind to a data model path; initialize with an empty string.',
        ),
        endValue: DynamicStringSchema.describe(
          'Range end date, ISO 8601 (YYYY-MM-DD). Bind to a data model path; initialize with an empty string.',
        ),
      }),
    },
    VIQSwitch: {
      description: 'VisitIQ on/off toggle switch. Prefer over CheckBox for enable/disable settings.',
      props: z.object({
        title: DynamicStringSchema.describe('Label shown next to the switch.').optional(),
        value: DynamicBooleanSchema.describe('Current on/off state. Bind to a boolean data model path.'),
      }),
    },
    VIQMultiTagInput: {
      description: 'VisitIQ multi-tag input: the user types values and presses comma to add tags.',
      props: z.object({
        label: DynamicStringSchema.describe('Label shown above the input.').optional(),
        value: DynamicStringListSchema.describe('The list of tags. Bind to a string-array data model path.'),
      }),
    },
    VIQMultiEmailInput: {
      description: 'VisitIQ multi-email input: the user types email addresses and presses comma to add them.',
      props: z.object({
        label: DynamicStringSchema.describe('Label shown above the input.').optional(),
        value: DynamicStringListSchema.describe('The list of email addresses. Bind to a string-array data model path.'),
      }),
    },
    VIQPhoneInput: {
      description: 'VisitIQ phone number input with country code selector.',
      props: z.object({
        label: DynamicStringSchema.describe('Label shown above the input.').optional(),
        value: DynamicStringSchema.describe(
          'The phone number (E.164-ish). Bind to a data model path; init with an empty string. Stays empty until ' +
            'the number is complete.',
        ),
      }),
    },
    VIQGeoFilter: {
      description:
        'VisitIQ geo-filter picker: a button that opens a map modal where the user builds one or ' +
        'more geographic filters (polygon / radius / zip|DMA list) for a Pulse audience. On confirm ' +
        'it writes a JSON string of the GraphQL PersonFilterInput[] the Pulse audience creation ' +
        'expects. Bind `value` to a data model path; initialize with an empty string.',
      props: z.object({
        label: DynamicStringSchema.describe('Button label. Defaults to a localized "Select geo filters".').optional(),
        value: DynamicStringSchema.describe(
          'JSON string of the selected PersonFilterInput[] geo filters. Bind to a data model path; ' +
            'init with an empty string. Empty until the user confirms the modal.',
        ),
      }),
    },
    VIQSecretInput: {
      description:
        'VisitIQ masked secret/credential input (password field). Use ONLY for sensitive fields (API keys, ' +
        'passwords) in a mutation-confirmation surface — the card encrypts the value before it leaves the browser.',
      props: z.object({
        label: DynamicStringSchema.describe('Label shown above the input.').optional(),
        value: DynamicStringSchema.describe(
          'The secret value. Bind to a data model path; initialize with an empty string.',
        ),
      }),
    },
  },
  {
    TextField: VisitiqTextField,
    CheckBox: VisitiqCheckBox,
    ChoicePicker: VisitiqChoicePicker,
    DateTimeInput: VisitiqDateTimeInput,
    Button: VisitiqButton,
    Text: VisitiqText,
    Row: VisitiqRow,
    Column: VisitiqColumn,
    Card: VisitiqCard,
    VIQDateRangePicker: VisitiqDateRangePicker,
    VIQSwitch: VisitiqSwitch,
    VIQMultiTagInput: VisitiqMultiTagInput,
    VIQMultiEmailInput: VisitiqMultiEmailInput,
    VIQPhoneInput: VisitiqPhoneInput,
    VIQGeoFilter: VisitiqGeoFilter,
    VIQSecretInput: VisitiqSecretInput,
  },
  { catalogId: VISITIQ_CATALOG_ID, includeBasicCatalog: true },
);
